import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Database, Upload, Loader2, Search, Trash2, FileText, AlertCircle, CheckCircle2, X } from "lucide-react";
import PeerWinsPanel from "@/components/PeerWinsPanel";

const money = (v) =>
  v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PricingHistory() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const fileRef = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/pricing-history/summary"),
        api.get("/pricing-history", { params: q ? { q } : {} }),
      ]);
      setSummary(s.data);
      setItems(l.data.items || []);
    } catch { /* no-op */ }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(() => { loadAll(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const uploadFile = async (file) => {
    if (!file) return;
    const okExt = /\.(csv|xlsx|xls)$/i.test(file.name);
    if (!okExt) {
      toast.error("Only .csv, .xls, and .xlsx are supported");
      return;
    }
    setUploading(true);
    setLastImport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/pricing-history/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastImport(r.data);
      toast.success(`Imported ${r.data.imported} rows from ${r.data.source_file}`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeRow = async (id) => {
    try {
      await api.delete(`/pricing-history/${id}`);
      setItems((its) => its.filter((r) => r.item_id !== id));
      setSummary((s) => (s ? { ...s, count: Math.max(0, s.count - 1) } : s));
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
  };

  const clearAll = async () => {
    if (!window.confirm("Delete ALL uploaded pricing history for this workspace? This cannot be undone.")) return;
    try {
      const r = await api.delete("/pricing-history", { data: { confirm: true } });
      toast.success(`Cleared ${r.data.deleted} rows`);
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not clear"); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) uploadFile(f);
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="flex items-center gap-4 mb-2">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
          <Database className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="text-[10px] font-mono font-bold tracking-widest text-amber-600">// PRICING HISTORY</div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900">Teach the Estimator your real prices</h1>
        </div>
      </div>
      <p className="text-slate-600 max-w-2xl mb-8">
        Upload past bids as CSV or Excel. The Estimating Agent will pull unit prices from these rows the next time it drafts an estimate — descriptions matching ≥ 80% get their unit price snapped directly to the historical value, and every close match gets fed to the LLM as context so it stops guessing.
      </p>

      {/* Upload zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`rounded-2xl border-2 border-dashed p-8 mb-6 text-center transition-all ${
          dragging ? "border-amber-500 bg-amber-50" : "border-slate-300 bg-white hover:border-amber-400"
        }`}
        data-testid="upload-dropzone"
      >
        <Upload className={`w-10 h-10 mx-auto mb-3 ${dragging ? "text-amber-600" : "text-slate-400"}`} />
        <div className="font-display font-bold text-lg text-slate-900 mb-1">
          {uploading ? "Uploading…" : "Drag a bid file here"}
        </div>
        <div className="text-sm text-slate-500 mb-4">CSV, XLSX, or XLS · up to 10 MB · 5,000 rows</div>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={(e) => uploadFile(e.target.files?.[0])}
          data-testid="upload-file-input"
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="upload-choose-btn"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? "UPLOADING" : "CHOOSE FILE"}
        </button>
        <div className="text-xs text-slate-400 mt-4">
          Expected headers: <code className="font-mono">Description</code>, <code className="font-mono">Unit</code>, <code className="font-mono">Qty</code>, <code className="font-mono">Unit Price</code>, <code className="font-mono">Amount</code>. Extra columns are ignored.
        </div>
      </div>

      {/* Last import summary */}
      {lastImport && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-6 flex items-start gap-3" data-testid="last-import-card">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold text-emerald-900">
              Imported {lastImport.imported} rows from {lastImport.source_file}
              {lastImport.skipped > 0 && <span className="text-emerald-700 font-normal"> · {lastImport.skipped} skipped</span>}
            </div>
            <div className="text-xs text-emerald-700 mt-1">
              Column mapping:
              {Object.entries(lastImport.column_mapping || {}).map(([k, v]) => (
                <span key={k} className="ml-2 inline-block font-mono">
                  {k}: <span className="font-bold">{v || "—"}</span>
                </span>
              ))}
            </div>
          </div>
          <button onClick={() => setLastImport(null)} className="text-emerald-600 hover:text-emerald-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary stats + search + clear */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500">ROWS</div>
          <div className="font-display font-black text-3xl text-slate-900" data-testid="summary-count">
            {summary?.count?.toLocaleString() ?? "—"}
          </div>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-4">
          <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500">FILES</div>
          <div className="font-display font-black text-3xl text-slate-900">{summary?.files?.length ?? "—"}</div>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-4 md:col-span-2">
          <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500">LAST UPLOAD</div>
          <div className="font-display font-bold text-lg text-slate-900 truncate">
            {summary?.latest_upload ? new Date(summary.latest_upload).toLocaleString() : "No uploads yet"}
          </div>
        </div>
      </div>

      {/* Search + clear */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search descriptions (e.g. roofing, mobilization)"
            className="w-full bg-white border border-slate-200 rounded-full pl-10 pr-4 py-2.5 text-sm focus:border-amber-400 outline-none"
            data-testid="pricing-search"
          />
        </div>
        {summary?.count > 0 && (
          <button
            onClick={clearAll}
            className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-rose-600 border border-slate-200 hover:border-rose-300 rounded-full px-4 py-2.5"
            data-testid="clear-all-btn"
          >
            CLEAR ALL
          </button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="empty-state">
            <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            {q ? `No rows match "${q}"` : "No pricing history uploaded yet. Drop a CSV above to get started."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pricing-history-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] font-mono font-bold tracking-widest text-slate-500">
                  <th className="px-4 py-3">DESCRIPTION</th>
                  <th className="px-4 py-3 w-20">UNIT</th>
                  <th className="px-4 py-3 w-24 text-right">QTY</th>
                  <th className="px-4 py-3 w-28 text-right">UNIT PRICE</th>
                  <th className="px-4 py-3 w-28 text-right">AMOUNT</th>
                  <th className="px-4 py-3 w-32">SOURCE</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={r.item_id} className={i % 2 ? "bg-slate-50/40" : "bg-white"} data-testid={`row-${i}`}>
                    <td className="px-4 py-3 text-slate-800">{r.description}</td>
                    <td className="px-4 py-3 text-slate-600 font-mono text-xs">{r.unit || "—"}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{r.qty ?? "—"}</td>
                    <td className="px-4 py-3 text-right font-mono font-bold text-emerald-700">{money(r.unit_price)}</td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">{money(r.amount)}</td>
                    <td className="px-4 py-3 text-xs text-slate-400 flex items-center gap-1.5">
                      <FileText className="w-3 h-3" />
                      <span className="truncate max-w-[140px]" title={r.source_file}>{r.source_file}</span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => removeRow(r.item_id)}
                        className="text-slate-300 hover:text-rose-500 transition-colors"
                        data-testid={`row-${i}-remove`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Peer wins calibration corpus */}
      <PeerWinsPanel />
    </div>
  );
}
